import React from "react";
import { FieldValues, Path, useController, useFormContext } from "react-hook-form";

type SharedInputProps<T extends FieldValues> = {
  data: { value: string; label: string }[];
  label?: string;
  name?: Path<T> | string;
  onChange: (payload: unknown) => void;
  value?: unknown;
  defaultValue?: string;
  withEmptyData?: boolean;
  required?: boolean;
  disabled?: boolean;
  helper?: string;
  error?: string | boolean;
  size?: "sm" | "md";
  w?: number;
  maw?: number;
  miw?: number;
};

export type BaseSelectInputControllerProps<
  T extends FieldValues,
  IP extends Record<string, unknown> = Record<string, unknown>,
> = {
  name: Path<T>;
  data: { value: string; label: string }[];
  label?: string;
  required?: boolean;
  onChange?: (value: string | null) => void;
  defaultValue?: string;
  disabled?: boolean;
  withEmptyData?: boolean;
  readOnly?: boolean;
  helper?: string;
  error?: string;
  size?: "sm" | "md";
  w?: number;
  maw?: number;
  miw?: number;
  InputComponent: React.ComponentType<IP & SharedInputProps<T>>;
  extractValue?: (payload: unknown) => string | null | undefined;
} & IP;

export const BaseSelectInputController = <
  T extends FieldValues,
  IP extends Record<string, unknown> = Record<string, unknown>,
>({
  label,
  data,
  required,
  name,
  onChange: onChangeCallback,
  defaultValue,
  disabled,
  withEmptyData = true,
  helper,
  error: customError,
  size = "md",
  w,
  maw,
  miw,
  InputComponent,
  extractValue = (v: unknown) => v as string | null | undefined,
  ...inputProps
}: BaseSelectInputControllerProps<T, IP>) => {
  const { control } = useFormContext<T>();
  const {
    field: { onChange, value },
    fieldState: { error },
  } = useController({ name, control, rules: { required } });

  return (
    <InputComponent
      data={data}
      label={label}
      name={name}
      onChange={(payload: unknown) => {
        const v = extractValue(payload);
        onChange(v);
        onChangeCallback?.(v ?? null);
      }}
      value={value}
      defaultValue={value ? undefined : defaultValue}
      withEmptyData={withEmptyData}
      required={required}
      disabled={disabled}
      helper={helper}
      // oxlint-disable-next-line typescript/no-base-to-string
      error={customError || error?.message?.toString() || !!error?.type}
      size={size}
      w={w}
      maw={maw}
      miw={miw}
      {...(inputProps as IP)}
    />
  );
};
